import React from "react";
import {
  Pencil,
  Trash,
  Eye,
  Plus,
  Search,
  Activity,
  ChevronsRight,
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useConfirm } from "../../context/ConfirmContext";
import { toast } from 'react-hot-toast';
import { useAdminAccountList, type Admin } from "../../hooks/useAdminAccountList";
import { useAdminMutations } from "../../hooks/useAdminMutations";
import { useQueryClient } from "@tanstack/react-query";
import axiosInstance from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/api/apiConfig";

const AdminAccount: React.FC = () => {
  const {
    admins,
    loading,
    error,
    totalPages,
    filters,
    setFilters,
  } = useAdminAccountList();

  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const { deleteAdmin } = useAdminMutations();
  const queryClient = useQueryClient();

  const prefetchAdminDetails = (id: number) => {
    queryClient.prefetchQuery({
      queryKey: ['user', String(id)],
      queryFn: async () => {
        const response = await axiosInstance.get(API_ENDPOINTS.ADMINS.GET_BY_ID(id));
        return response.data;
      },
    });
  };

  const handleViewAdmin = (adminId: number) => {
    navigate(`/admin/dashboard/account/admin/view/${adminId}`);
  };

  const handleDeleteAdmin = async (adminId: number) => {
    const isConfirmed = await confirm("Êtes-vous sûr de vouloir supprimer cet administrateur ?", {
      title: "Supprimer l'administrateur",
      confirmText: "Supprimer",
      cancelText: "Annuler"
    });

    if (!isConfirmed) return;

    deleteAdmin.mutate(adminId, {
      onSuccess: () => {
        toast.success("Administrateur supprimé avec succès");
      },
      onError: () => {
        toast.error("Erreur lors de la suppression de l'administrateur");
      }
    });
  };

  const handleUpdateAdmin = (adminId: number) => {
    navigate(`/admin/dashboard/account/admin/edit/${adminId}`);
  };

  const formatDate = (dateString: string) =>
    dateString ? new Date(dateString).toLocaleDateString("fr-FR") : "";

  const paginate = (pageNumber: number) => setFilters({ currentPage: pageNumber });
  const nextPage = () => setFilters({ currentPage: Math.min(filters.currentPage + 1, totalPages) });
  const prevPage = () => setFilters({ currentPage: Math.max(filters.currentPage - 1, 1) });
  const firstPage = () => setFilters({ currentPage: 1 });
  const lastPage = () => setFilters({ currentPage: totalPages });


  if (loading && admins.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="text-center">
          <div className="relative">
            <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4 mx-auto animate-pulse">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div className="absolute inset-0 w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full animate-ping opacity-20 mx-auto"></div>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Chargement des admins
          </h2>
          <p className="text-gray-600">Préparation des données...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-red-100 animate-fadeIn">
          <h2 className="text-xl font-semibold text-red-600 mb-2">{error}</h2>
          <p className="text-gray-600">Veuillez réessayer plus tard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-lg border-b border-slate-200/50 px-6 py-4 mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <Activity className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Gestion des Admins
              </h1>
              <p className="text-gray-600 text-sm">
                Visualisez et gérez les comptes administrateurs.
              </p>
            </div>
          </div>
          <Link
            to="/admin/dashboard/account/admin/new"
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors shadow-lg"
          >
            <Plus className="w-4 h-4" />
            <span className="text-sm font-medium">Nouveau Admin</span>
          </Link>
        </div>
      </div>

      <div className="px-6">
        <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 max-w-6xl mx-auto relative min-h-[400px]">
          {loading && admins.length > 0 && (
            <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center rounded-2xl">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-100 border-t-blue-600 mb-3"></div>
                <p className="text-sm text-blue-600 font-medium">
                  Actualisation...
                </p>
              </div>
            </div>
          )}
          {/* Search Filter */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="relative flex-grow">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={18}
              />
              <input
                type="text"
                placeholder="Rechercher par nom, téléphone ou email..."
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50 text-gray-800 placeholder-gray-500 transition-colors"
                value={filters.searchTerm}
                onChange={(e) => {
                  setFilters({ searchTerm: e.target.value });
                }}
              />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full table-auto divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {[
                    { label: "Nom", class: "md:w-1/6" },
                    { label: "Prénom", class: "md:w-1/6" },
                    {
                      label: "Date de naissance",
                      class: "hidden lg:table-cell md:w-1/12",
                    },
                    { label: "Genre", class: "hidden lg:table-cell md:w-1/12" },
                    { label: "Téléphone", class: "md:w-1/6" },
                    { label: "Email", class: "md:w-1/6" },
                    {
                      label: "Adresse",
                      class: "hidden lg:table-cell md:w-1/6",
                    },
                    { label: "Actions", class: "md:w-1/12" },
                  ].map((header, index) => (
                    <th
                      key={index}
                      className={`px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${header.class}`}
                    >
                      {header.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.length > 0 ? (
                  admins.map((admin: Admin) => (
                    <tr
                      key={admin.id}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900 text-sm">
                        {admin.lastName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {admin.firstName}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {formatDate(admin.dateOfBirth)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm hidden lg:table-cell">
                        {admin.gender === "M"
                          ? "Masculin"
                          : admin.gender === "F"
                            ? "Féminin"
                            : "Autre"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-sm">
                        {admin.phone}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm truncate max-w-[150px]">
                        {admin.email}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm hidden lg:table-cell truncate max-w-[150px]">
                        {admin.address}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-2">
                          <button
                            className="p-1.5 rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-800 transition-colors"
                            onClick={() => handleViewAdmin(admin.id)}
                            onMouseEnter={() => prefetchAdminDetails(admin.id)}
                            aria-label="Voir l'admin"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-yellow-50 text-yellow-600 hover:bg-yellow-100 hover:text-yellow-800 transition-colors"
                            onClick={() => handleUpdateAdmin(admin.id)}
                            aria-label="Modifier l'admin"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="p-1.5 rounded-full bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 transition-colors"
                            onClick={() => handleDeleteAdmin(admin.id)}
                            aria-label="Supprimer l'admin"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-4 py-3 text-center text-gray-500 text-sm"
                    >
                      Aucun admin trouvé
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex flex-col md:flex-row justify-between items-center">
            <div className="text-sm text-gray-600 mb-4 md:mb-0">
              Affichage de la page {filters.currentPage} sur {totalPages}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={firstPage}
                disabled={filters.currentPage === 1}
                className={`p-1.5 rounded-md ${filters.currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Première page"
              >
                <ChevronsLeft size={16} />
              </button>
              <button
                onClick={prevPage}
                disabled={filters.currentPage === 1}
                className={`p-1.5 rounded-md ${filters.currentPage === 1
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page précédente"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (filters.currentPage <= 3) {
                  pageNum = i + 1;
                } else if (filters.currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = filters.currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => paginate(pageNum)}
                    className={`w-8 h-8 rounded-md text-sm ${filters.currentPage === pageNum
                      ? "bg-blue-600 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                      }`}
                    aria-label={`Page ${pageNum}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={nextPage}
                disabled={filters.currentPage === totalPages}
                className={`p-1.5 rounded-md ${filters.currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Page suivante"
              >
                <ChevronRight size={16} />
              </button>
              <button
                onClick={lastPage}
                disabled={filters.currentPage === totalPages}
                className={`p-1.5 rounded-md ${filters.currentPage === totalPages
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-gray-700 hover:bg-gray-100"
                  }`}
                aria-label="Dernière page"
              >
                <ChevronsRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAccount;
